import { Alert, Button, Divider, Form, Image, Input, message, Modal, Select, SelectProps, Space, Tag } from "antd";
import { useEffect, useState } from "react";
import { useForm } from "antd/lib/form/Form";
import { useAuth } from "../../Contexts/AuthContext";
import { BookyInput } from "../Forms/BookyInput";
import { GroupService } from "../../Services/GroupService";
import { TextService } from "../../Services/TextService";
import { MemberService } from "../../Services/MemberService";
import { WebApiCompanyDtoCompanyDto, WebApiMemberDtoMemberDto, WebApiModelsEmailTemplate, WebApiModelsMemberField, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiServicesDataTransferObjectsMemberServiceFilterLogic } from "../../Repository/eventbookyapi";
import FileSelect from "../Upload/UploadFile";
import { UploadChangeParam } from "antd/lib/upload";
import { BookyButton } from "../Buttons/BookyButton";
import { MemberAutocomplete } from "../AutoComplete/MemberAutocomplete";
import { MemberTable } from "../Table/MemberTable";
import { MemberPaymentStatus } from "../../Types/MemberPaymentStatus";
import { BookySelectPaymentStatus } from "../Forms/BookyPaymentStatusSelect";
import { EmailFormType } from "../../Types/EmailFormType";
import { OneFrontHtmlEditor } from "../Forms/OneFrontHtmlEditor";
import { CompanyService } from "../../Services/CompanyService";
import { BookySelectDropDown } from "../Forms/BookySelectDropDown";
import { ContactAutoComplete } from "../AutoComplete/ContactAutocomplete";
import { EmailTemplateService } from "../../Services/EmailTemplateService";
import { PaymentConfiguration } from "../../Types/PaymentConfiguration";
import { BookyCheckbox } from "../Forms/BookyCheckbox";
import { EmailNotificationPreference } from "../../Types/EmailNotificationPreference";
import { CheckCircleFilled, CheckCircleOutlined, ExclamationCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { AutoCompleteType } from "../../Types/AutoCompleteType";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { MemberFieldService } from "../../Services/MemberFieldService";
import { FilterOperator } from "../../Types/FilterOperator";
import { AdvancedMemberFilter, FilterRequest } from "../Filters/AdvancedMemberFilter";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";
import { EmailTemplateType } from "../../Types/EmailTemplate";
import { BlobDto } from "../../Repository/eventbookyapi";
import ImageSelect from "../Select/ImageSelect";
import EmailDesignTemplateCardSelect from "../Select/EmailDesignTemplateCardSelect";
import { DocumentService } from "../../Services/ImageService";
import ColorPicker from "../Color/ColorPicker";
import { FamilyMemberConfiguration } from "../../Types/FamilyMemberConfiguration";
import { filterByRecipientType, RecipientTypeFilter } from "../../Helpers/FamilyMemberHelper";

const DEFAULT_ACCENT_COLOR = "#4a6cf7";
const DEFAULT_TEXT_COLOR = "#ffffff";

const resolveEmailDesignTemplate = (template?: WebApiModelsEmailTemplate): EmailTemplateType | undefined => {
    if (!template) {
        return undefined;
    }

    const hasHeaderDesign = !!(template.header || template.accentColor || template.textColor);
    const hasLogoDesign = !!template.imageUrl;
    const templateType = template.emailDesignTemplate as EmailTemplateType | undefined;

    // Prefer concrete field data because older clients could save shifted enum values.
    if (hasHeaderDesign) {
        return EmailTemplateType.Header;
    }

    if (hasLogoDesign) {
        return EmailTemplateType.Classic;
    }

    if (templateType === EmailTemplateType.Header) {
        return EmailTemplateType.Header;
    }

    if (templateType === EmailTemplateType.Minimal) {
        return EmailTemplateType.Minimal;
    }

    if (templateType === EmailTemplateType.Classic) {
        // Classic without logo is most likely old Minimal data saved with wrong enum index.
        return EmailTemplateType.Minimal;
    }

    return undefined;
}

export const EmailForm = (props: any) => {
    const [form] = useForm();
    const [initialHtml, setInitialHtml] = useState("" as any);
    const [html, setHtml] = useState("" as any);
    const [externalEmails, setExternalEmails] = useState([] as any[]);
    const [subject, setSubject] = useState("" as string);
    const [editorKey, setEditorKey] = useState(0);
    const [emailIsvalid, setEmailIsValid] = useState(false as boolean);

    const [company, setCompany] = useState({} as WebApiCompanyDtoCompanyDto);

    const [loading, setLoading] = useState(false as boolean);
    const [showConfirm, setShowConfirm] = useState(false as boolean);
    const [previewLoading, setPreviewLoading] = useState(false as boolean);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false as boolean);
    const [isDesignInfoModalOpen, setIsDesignInfoModalOpen] = useState(false as boolean);
    const [previewHtml, setPreviewHtml] = useState("" as string);
    const [previewRenderKey, setPreviewRenderKey] = useState(0);
    const [selectedGroup, setSelectedGroup] = useState([] as number[]);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(undefined as MemberPaymentStatus | undefined);

    const [fileResult, setFileList] = useState<UploadChangeParam>();
    const [fileKey, setFileKey] = useState(0);
    const [isAllowedToSendGroupMails, setIsAllowedToSendGroupMails] = useState(true as boolean);
    const { user } = useAuth();
    const [selectedMembers, setSelectedMembers] = useState([] as WebApiMemberDtoMemberDto[]);
    const [recipientType, setRecipientType] = useState<RecipientTypeFilter>("all");
    const [memberFieldSettings, setMemberFieldSettings] = useState([] as WebApiModelsMemberField[]);
    const [currentFilterRequest, setCurrentFilterRequest] = useState<FilterRequest | undefined>(undefined);
    const [images, setImages] = useState([] as BlobDto[]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false as boolean);
    const [selectedLogo, setSelectedLogo] = useState("" as string);
    const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR as string);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR as string);
    const [selectedMembershipProductIds, setSelectedMembershipProductIds] = useState([] as number[]);
    const [isTestSendModalOpen, setIsTestSendModalOpen] = useState(false as boolean);
    const [testSendMember, setTestSendMember] = useState<WebApiMemberDtoMemberDto | undefined>(undefined);
    const [testSendExternalEmail, setTestSendExternalEmail] = useState("" as string);
    const [testSendLoading, setTestSendLoading] = useState(false as boolean);
    const [testSendMembers, setTestSendMembers] = useState([] as WebApiMemberDtoMemberDto[]);

    const GetText = TextService.GetText;
    const familyMembersEnabled = user?.familyMemberConfiguration === FamilyMemberConfiguration.Enabled;
    const hasEmailDesign = Form.useWatch("enableEmailDesign", form) === true;
    const selectedEmailDesignTemplate = Form.useWatch("emailDesignTemplate", form) as EmailTemplateType | undefined;

    useEffect(() => {
        if (props.groups != null && props.groups.length > 0) {
            setSelectedGroup(props.groups[0]);
            form.setFieldValue("group", props.groups[0].id)
        }

        CompanyService.GetCompany().then(company => {
            setCompany(company);
        })

        MemberFieldService.GetMemberFields().then(result => {
            setMemberFieldSettings(result);
        });

        GroupService.IsAllowedToSendGroupMails().then(result => {
            setIsAllowedToSendGroupMails(result.toString() === true.toString());
        });
    }, [props]);

    const containsParams = (htmlContent: string): boolean => {
        return htmlContent.indexOf('{{params.') !== -1;
    };

    const containsPaymentLinkParam = (htmlContent: string): boolean => {
        if (!htmlContent) return false;
        const lower = htmlContent.toLowerCase();
        // Swedish: {{params.betallänk}}, {{params.betallänkföregåendeår}}, {{params.betallänknästaår}}
        // English: {{params.paymentlink}}, {{params.paymenturlastyear}}, {{params.paymenturlnextyear}}
        return lower.includes('{{params.betallänk}}') || lower.includes('{{params.paymentlink}}')
            || lower.includes('{{params.betallänkföregåendeår}}') || lower.includes('{{params.paymenturlastyear}}')
            || lower.includes('{{params.betallänknästaår}}') || lower.includes('{{params.paymenturlnextyear}}');
    };

    const getRequiredPaymentLinkTypes = (htmlContent: string): { currentYear: boolean; lastYear: boolean; nextYear: boolean } => {
        if (!htmlContent) return { currentYear: false, lastYear: false, nextYear: false };
        const lower = htmlContent.toLowerCase();
        return {
            currentYear: lower.includes('{{params.betallänk}}') || lower.includes('{{params.paymentlink}}'),
            lastYear: lower.includes('{{params.betallänkföregåendeår}}') || lower.includes('{{params.paymenturlastyear}}'),
            nextYear: lower.includes('{{params.betallänknästaår}}') || lower.includes('{{params.paymenturlnextyear}}'),
        };
    };

    const getMembersWithoutPaymentLink = (): WebApiMemberDtoMemberDto[] => {
        if (!containsPaymentLinkParam(html) || selectedMembers.length === 0) return [];

        const requiredTypes = getRequiredPaymentLinkTypes(html);

        return selectedMembers.filter(m => {
            if (requiredTypes.currentYear && (!m.paymentLink || m.paymentLink.length === 0)) {
                return true;
            }
            if (requiredTypes.lastYear && (!m.paymentLinkLastYear || m.paymentLinkLastYear.length === 0)) {
                return true;
            }
            if (requiredTypes.nextYear && (!m.paymentLinkNextYear || m.paymentLinkNextYear.length === 0)) {
                return true;
            }
            return false;
        });
    };

    const onReminderFinish = async (formValues: any) => {

        if (hasNoMembersOrEmails()) {
            message.warning(GetText("EmailForm_NoRecipientsSelected"));
            return;
        }

        if (htmlMessageIsEmpty(html)) {
            message.warning(GetText("EmailForm_MessageRequired"));
            return;
        }

        if (stringIsEmpty(subject)) {
            message.warning(GetText("EmailForm_SubjectRequired"));
            return;
        }

        if (props.type === EmailFormType.EmailFormByExternal && containsParams(html)) {
            message.error(GetText("EmailForm_CannotUseParamsExternal"));
            return;
        }

        setShowConfirm(true);
    };

    const onFileSelectChange = (files: any) => {
        setFileList(files);
    }

    const loadImages = async () => {
        const publicImages = await DocumentService.GetPublicImages();
        setImages(publicImages);
    }

    const openImagePicker = async () => {
        await loadImages();
        setIsImageModalOpen(true);
    }

    const onImageSelect = (image: string) => {
        setSelectedLogo(image);
        form.setFieldValue("imageUrl", image);
    }

    const onEmailDesignToggle = (event: any) => {
        if (event?.target?.checked !== true) {
            clearEmailDesignFields();
            return;
        }

        form.setFieldValue("emailDesignTemplate", EmailTemplateType.Classic);
    }

    const clearEmailDesignFields = () => {
        form.setFieldValue("enableEmailDesign", false);
        form.setFieldValue("emailDesignTemplate", undefined);
        form.setFieldValue("header", undefined);
        form.setFieldValue("imageUrl", undefined);
        form.setFieldValue("accentColor", undefined);
        form.setFieldValue("textColor", undefined);

        setSelectedLogo("");
        setAccentColor(DEFAULT_ACCENT_COLOR);
        setTextColor(DEFAULT_TEXT_COLOR);
    }

    const onEmailDesignTemplateChange = (templateType: EmailTemplateType) => {

        if (templateType === EmailTemplateType.Classic) {
            form.setFieldValue("emailDesignTemplate", 1);
        }

        if (templateType === EmailTemplateType.Minimal) {
            form.setFieldValue("emailDesignTemplate", 2);
        }

        if (templateType === EmailTemplateType.Header) {
            form.setFieldValue("emailDesignTemplate", 3);
        }

        if (templateType !== EmailTemplateType.Classic) {
            form.setFieldValue("imageUrl", undefined);
            setSelectedLogo("");
        }

        if (templateType !== EmailTemplateType.Header) {
            form.setFieldValue("header", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
        }

        if (templateType === EmailTemplateType.Header) {
            const initialAccentColor = form.getFieldValue("accentColor") || DEFAULT_ACCENT_COLOR;
            const initialTextColor = form.getFieldValue("textColor") || DEFAULT_TEXT_COLOR;
            form.setFieldValue("accentColor", initialAccentColor);
            form.setFieldValue("textColor", initialTextColor);
            setAccentColor(initialAccentColor);
            setTextColor(initialTextColor);
        }
    }

    const onAccentColorChanged = (hex: string) => {
        setAccentColor(hex);
        form.setFieldValue("accentColor", hex);
    }

    const onTextColorChanged = (hex: string) => {
        setTextColor(hex);
        form.setFieldValue("textColor", hex);
    }

    const setDesignFromTemplate = (template?: WebApiModelsEmailTemplate) => {
        const resolvedTemplateType = resolveEmailDesignTemplate(template);

        if (!template || resolvedTemplateType === undefined) {
            clearEmailDesignFields();
            return;
        }

        form.setFieldValue("enableEmailDesign", true);
        form.setFieldValue("emailDesignTemplate", resolvedTemplateType);

        if (resolvedTemplateType === EmailTemplateType.Classic) {
            form.setFieldValue("imageUrl", template.imageUrl);
            setSelectedLogo(template.imageUrl || "");
        } else {
            form.setFieldValue("imageUrl", undefined);
            setSelectedLogo("");
        }

        if (resolvedTemplateType === EmailTemplateType.Header) {
            const initialAccentColor = template.accentColor || DEFAULT_ACCENT_COLOR;
            const initialTextColor = template.textColor || DEFAULT_TEXT_COLOR;

            form.setFieldValue("header", template.header);
            form.setFieldValue("accentColor", initialAccentColor);
            form.setFieldValue("textColor", initialTextColor);

            setAccentColor(initialAccentColor);
            setTextColor(initialTextColor);
        } else {
            form.setFieldValue("header", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
        }
    }

    const getDesignPayload = () => {
        const includeDesign = form.getFieldValue("enableEmailDesign") === true;
        const emailDesignTemplate = includeDesign
            ? form.getFieldValue("emailDesignTemplate") as EmailTemplateType | undefined
            : undefined;

        const isClassicTemplate = emailDesignTemplate === EmailTemplateType.Classic;
        const isHeaderTemplate = emailDesignTemplate === EmailTemplateType.Header;

        console.log(form.getFieldValue("emailDesignTemplate"));

        return {
            designHeader: isHeaderTemplate ? form.getFieldValue("header") : undefined,
            designImageUrl: isClassicTemplate ? form.getFieldValue("imageUrl") : undefined,
            designAccentColor: isHeaderTemplate ? form.getFieldValue("accentColor") : undefined,
            designTextColor: isHeaderTemplate ? form.getFieldValue("textColor") : undefined,
            designEmailDesignTemplate: form.getFieldValue("emailDesignTemplate"),
        };
    }

    const getPreviewPayload = () => {
        const includeDesign = form.getFieldValue("enableEmailDesign") === true;
        const emailDesignTemplate = includeDesign
            ? form.getFieldValue("emailDesignTemplate") as EmailTemplateType | undefined
            : undefined;

        const isClassicTemplate = emailDesignTemplate === EmailTemplateType.Classic;
        const isHeaderTemplate = emailDesignTemplate === EmailTemplateType.Header;

        return {
            webApiMemberDtoPreviewMailBodyDto: {
                message: html,
                design: emailDesignTemplate === undefined
                    ? undefined
                    : {
                        header: isHeaderTemplate ? form.getFieldValue("header") : undefined,
                        imageUrl: isClassicTemplate ? form.getFieldValue("imageUrl") : undefined,
                        accentColor: isHeaderTemplate ? form.getFieldValue("accentColor") : undefined,
                        textColor: isHeaderTemplate ? form.getFieldValue("textColor") : undefined,
                        emailDesignTemplate: form.getFieldValue("emailDesignTemplate"),
                    },
            },
        };
    }

    const normalizePreviewHtml = (content: string) => {
        const trimmedContent = content?.trim();
        if (!trimmedContent) {
            return "";
        }

        // Some environments return HTML as a JSON-encoded string: "<html>..."
        if (trimmedContent.startsWith('"') && trimmedContent.endsWith('"')) {
            try {
                const parsedContent = JSON.parse(trimmedContent);
                if (typeof parsedContent === "string") {
                    return parsedContent;
                }
            } catch {
                return content;
            }
        }

        return content;
    }

    const onPreviewEmail = async () => {
        if (htmlMessageIsEmpty(html)) {
            message.warning(GetText("EmailForm_MessageRequired"));
            return;
        }

        setPreviewHtml("");
        setIsPreviewModalOpen(true);
        setPreviewLoading(true);

        try {
            const previewResult = await EmailTemplateService.PreviewEmail(getPreviewPayload());
            const normalizedPreviewHtml = normalizePreviewHtml(previewResult);
            setPreviewHtml(normalizedPreviewHtml && normalizedPreviewHtml.length > 0 ? normalizedPreviewHtml : html);
            setPreviewRenderKey((previous) => previous + 1);
        } catch {
            setIsPreviewModalOpen(false);
            message.error(GetText("EmailForm_PreviewFailed"));
        } finally {
            setPreviewLoading(false);
        }
    }

    const onOpenTestSendModal = async () => {
        if (htmlMessageIsEmpty(html)) {
            message.warning(GetText("EmailForm_MessageRequired"));
            return;
        }

        if (stringIsEmpty(subject)) {
            message.warning(GetText("EmailForm_SubjectRequired"));
            return;
        }

        // Get available members for test send
        let membersForTestSend: WebApiMemberDtoMemberDto[] = [];

        if (props.type === EmailFormType.EmailFormByGroup) {
            // Fetch all members from the system for test send (not just the filtered sendlist)
            try {
                const allMembers = await MemberService.GetFilteredMembers({
                    status: undefined,
                    emailNotificationPreference: EmailNotificationPreference.Unspecified,
                    isAllMembers: true,
                    groupsIds: selectedGroup.length > 0 ? selectedGroup : [],
                    filters: [{ fieldName: "Email", operator: FilterOperator.IsNotEmpty, value: undefined, logic: 0 as WebApiServicesDataTransferObjectsMemberServiceFilterLogic }],
                    membershipProductIds: undefined,
                });
                membersForTestSend = allMembers;
            } catch {
                membersForTestSend = [];
            }
        } else if (props.type === EmailFormType.EmailFormByMember && props.memberResult?.members) {
            membersForTestSend = props.memberResult.members;
        }

        setTestSendMembers(membersForTestSend);
        setTestSendMember(undefined);
        setTestSendExternalEmail("");
        setIsTestSendModalOpen(true);

        // Try to pre-select logged-in user's member if available
        if (user?.email) {
            const userMember = membersForTestSend.find(m => m.email?.toLowerCase() === user.email?.toLowerCase());
            if (userMember) {
                setTestSendMember(userMember);
            }
        }
    }

    const onTestSendConfirm = async () => {
        if (!testSendMember && stringIsEmpty(testSendExternalEmail)) {
            message.error(GetText("EmailForm_SelectAtLeastOneMember"));
            return;
        }

        setTestSendLoading(true);

        try {
            const designPayload = getDesignPayload();

            if (testSendMember) {
                // Send to member
                await MemberService.SendEmailToMembers({
                    subject: form.getFieldValue("subject"),
                    message: html,
                    files: getFileFromUploadControl(),
                    memberIds: [testSendMember.memberId!],
                    ...designPayload,
                });
            } else {
                // Send to external email
                await EmailTemplateService.SendMailToExternal({
                    subject: form.getFieldValue("subject"),
                    message: html,
                    files: getFileFromUploadControl(),
                    emails: [testSendExternalEmail],
                    ...designPayload,
                });
            }

            message.success(GetText("EmailForm_EmailsSent"));
            setIsTestSendModalOpen(false);
            setTestSendMember(undefined);
            setTestSendExternalEmail("");
        } catch {
            message.error(GetText("EmailForm_EmailsSentFailed"));
        } finally {
            setTestSendLoading(false);
        }
    }

    const onTestSendCancel = () => {
        setIsTestSendModalOpen(false);
        setTestSendMember(undefined);
        setTestSendExternalEmail("");
    }

    const onTestSendMemberSelect = (member: WebApiMemberDtoMemberDto) => {
        setTestSendMember(member);
        setTestSendExternalEmail("");
    }

    const onFilterMembers = async (filterRequest?: FilterRequest) => {
        let filteredMembers: WebApiMemberDtoMemberDto[] = [];

        if (!selectedGroup.length) {
            message.warning(GetText("EmailForm_SelectOneGroup"));
            return;
        }

        // Prepare filters array - always include Email IsNotEmpty
        const baseFilters = filterRequest && filterRequest.filters.length > 0
            ? filterRequest.filters
            : [{ fieldName: "Email", operator: FilterOperator.IsNotEmpty }];

        // Map the filters to API format (converting logic from string to enum)
        const filters = baseFilters.map(filter => ({
            fieldName: filter.fieldName,
            operator: filter.operator,
            value: filter.value,
            logic: (filter.logic === 'Or' ? 1 : 0) as WebApiServicesDataTransferObjectsMemberServiceFilterLogic
        }));

        // If we have advanced filters, ensure Email is not empty is included
        if (filterRequest && filterRequest.filters.length > 0) {
            const hasEmailFilter = filters.some(f => f.fieldName === "Email" && f.operator === FilterOperator.IsNotEmpty);
            if (!hasEmailFilter) {
                filters.push({
                    fieldName: "Email",
                    operator: FilterOperator.IsNotEmpty,
                    value: undefined,
                    logic: 0 as WebApiServicesDataTransferObjectsMemberServiceFilterLogic
                });
            }
        }

        filteredMembers = await MemberService.GetFilteredMembers({
            status: selectedPaymentStatus,
            emailNotificationPreference: form.getFieldValue("emailNotificationPreference") == 0 || !form.getFieldValue("emailNotificationPreference") ? EmailNotificationPreference.Unspecified : EmailNotificationPreference.NoInformationEmails,
            isAllMembers: selectedGroup.length == 0 || selectedGroup.some(p => p == -1) ? true : false,
            groupsIds: selectedGroup,
            filters: filters,
            membershipProductIds: selectedMembershipProductIds.length > 0 ? selectedMembershipProductIds : undefined,
        });

        if (familyMembersEnabled) {
            filteredMembers = filterByRecipientType(filteredMembers, recipientType);
        }

        setSelectedMembers(filteredMembers);

        if (filteredMembers.length == 0) {
            message.warning(GetText("EmailForm_NoMembersFound"))
        }
    }

    const handleAdvancedFilterChange = (filterRequest: FilterRequest) => {
        // Just store the filter request, don't apply it yet
        setCurrentFilterRequest(filterRequest);
    };



    const onSendConfirmOK = async () => {
        setLoading(true);

        if (selectedMembers.length == 0) {
            message.error(GetText("EmailForm_SelectAtLeastOneMember"));
            return;
        }

        const memberIds: number[] = selectedMembers.map(m => m.memberId!);
        const designPayload = getDesignPayload();

        try {
            await MemberService.SendEmailToMembers({
                subject: form.getFieldValue("subject"),
                message: html,
                files: getFileFromUploadControl(),
                memberIds: memberIds,
                ...designPayload,
            });
            message.success(GetText("EmailForm_EmailsSent"));
        } catch {
            message.error(GetText("EmailForm_EmailsSentFailed"))
        }

        setLoading(false);
        form.setFieldsValue({
            message: "",
            messageFromTemplate: "",
            subject: "",
            header: undefined,
            imageUrl: undefined,
            accentColor: undefined,
            textColor: undefined,
            upload: undefined,
        });
        clearEmailDesignFields();
        setSubject("");
        setHtml("");
        setInitialHtml(undefined);
        setEditorKey(prev => prev + 1);
        setFileList(undefined);
        setFileKey(prev => prev + 1);
        setShowConfirm(false);
    };

    const getFileFromUploadControl = (): Blob[] => {
        var files: Blob[] = [];

        fileResult?.fileList.forEach((file: any) => {
            if (file.originFileObj) {
                files.push(file.originFileObj as Blob);
            }
            else {
                files.push(file as Blob);
            }
        });

        return files;
    }

    const onSendConfirmOKExternal = async () => {
        setLoading(true);

        if (externalEmails.length == 0) {
            message.error(GetText("EmailForm_SelectAtLeastOneEmail"));
            return;
        }

        const designPayload = getDesignPayload();

        try {
            await EmailTemplateService.SendMailToExternal({
                subject: form.getFieldValue("subject"),
                message: html,
                files: getFileFromUploadControl(),
                emails: externalEmails,
                ...designPayload,
            });
            message.success(GetText("EmailForm_EmailsSent"));
        } catch {
            message.error(GetText("EmailForm_EmailsSentFailed"))
        }

        setLoading(false);
        form.setFieldsValue({
            message: "",
            messageFromTemplate: "",
            subject: "",
            header: undefined,
            imageUrl: undefined,
            accentColor: undefined,
            textColor: undefined,
            upload: undefined,
        });
        clearEmailDesignFields();
        setSubject("");
        setHtml("");
        setInitialHtml(undefined);
        setEditorKey(prev => prev + 1);
        setFileList(undefined);
        setFileKey(prev => prev + 1);
        setShowConfirm(false);
    }

    const onAutoCompleteSelect = (member: WebApiMemberDtoMemberDto) => {
        let newList: WebApiMemberDtoMemberDto[] = selectedMembers.map(p => p);
        if (!selectedMembers.some((e: WebApiMemberDtoMemberDto) => e.email === member.email)) {
            newList.push(member);
        }
        setSelectedMembers(newList);
    }

    const removeMember = (memberToRemove: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto) => {
        let newList = selectedMembers.filter(p => p.email != memberToRemove.email);
        setSelectedMembers(newList);
    };


    const groupOptions = () => {
        const options: SelectProps['options'] = [];

        props.groups.forEach((element: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
            options.push({
                label: element.name,
                value: element.id,
            });
        });

        return options;
    }

    const emailTemplatesOptions = () => {
        const options: SelectProps['options'] = [];

        props.emailTemplates.forEach((element: WebApiModelsEmailTemplate) => {
            options.push({
                label: element.templateName,
                value: element.id,
            });
        });

        return options;
    }

    const handleGroupChange = async (value: number[]) => {
        setSelectedGroup(value);
    };

    const handleEmailTemplateChange = async (value: number) => {
        let emailTemplate: WebApiModelsEmailTemplate[] = props.emailTemplates.filter((p: WebApiModelsEmailTemplate) => p.id === value);
        if (emailTemplate && emailTemplate.length > 0) {
            setInitialHtml(emailTemplate[0]?.body);
            setHtml(emailTemplate[0]?.body);
            setSubject(emailTemplate[0]?.subject || "");
            setEditorKey(prev => prev + 1);
            setDesignFromTemplate(emailTemplate[0]);

            form.setFieldsValue({
                message: emailTemplate[0]?.body,
                messageFromTemplate: emailTemplate[0]?.body,
                subject: emailTemplate[0]?.subject
            });

            const emailContainsEmails = stringIsEmpty(emailTemplate[0]?.subject!) || htmlMessageIsEmpty(emailTemplate[0]?.body!);
            setEmailIsValid(!emailContainsEmails);
        }
        else {
            setInitialHtml(undefined);
            setSubject("");
            setEditorKey(prev => prev + 1);
            clearEmailDesignFields();
            form.setFieldsValue({ subject: "" });
        }
    };

    const onSendConfirmCancel = async () => {
        setShowConfirm(false);
    };

    const onSelectPaymentChanged = async (value: MemberPaymentStatus) => {
        setSelectedPaymentStatus(value);
    };

    const onHtmlChange = (html: string) => {
        const emailContainsEmails = stringIsEmpty(subject) || htmlMessageIsEmpty(html);
        setEmailIsValid(!emailContainsEmails);
        form.setFieldValue("message", html);
        setHtml(html);
    }

    const onEmailsUpdated = async (onEmailsUpdated: any) => {
        setExternalEmails(onEmailsUpdated => ([
            ...onEmailsUpdated,
        ]));
    }

    const hasNoMembersOrEmails = () => {
        return (selectedMembers.length == 0 && externalEmails.length == 0)
    }

    const htmlMessageIsEmpty = (html: string) => {
        return html.length < 1 || html == "<p></p>"
    }

    const stringIsEmpty = (subject: string) => {
        return subject === "";
    }

    const onSubjectchange = (e: any) => {
        const emailContainsEmails = stringIsEmpty(e.target.value) || htmlMessageIsEmpty(html);
        setEmailIsValid(!emailContainsEmails);
        setSubject(e.target.value);
    }

    const designInfoLabel = <Space size={6}>
        <span>{GetText("CreateTemplate_AddDesign")}</span>
        <InfoCircleOutlined
            style={{ color: "#1677ff", cursor: "pointer" }}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDesignInfoModalOpen(true);
            }}
        />
    </Space>;

    return <>
        <Modal
            title={GetText("EmailForm_ConfirmText")}
            open={showConfirm}
            onOk={props.type === EmailFormType.EmailFormByExternal ? onSendConfirmOKExternal : onSendConfirmOK}
            confirmLoading={loading}
            onCancel={onSendConfirmCancel}
        >
            {props.type === EmailFormType.EmailFormByExternal ?
                (GetText("EmailForm_ConfirmText1") + externalEmails.length + GetText("EmailForm_ConfirmText2")) :
                (GetText("EmailForm_ConfirmText1") + selectedMembers.length + GetText("EmailForm_ConfirmText2"))}
            {getMembersWithoutPaymentLink().length > 0 && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleFilled style={{ color: '#faad14' }} />}
                    message={GetText("EmailForm_PaymentLinkWarningConfirm").replace("{0}", getMembersWithoutPaymentLink().length.toString())}
                />
            )}
        </Modal>
        {user?.hasPaidSubscription ?
            <>
                {isAllowedToSendGroupMails === true ? <Form
                    form={form}
                    layout="vertical"
                    onFinish={onReminderFinish}
                    autoComplete="off"
                >
                    <Form.Item
                    >
                        {company && company.email && company.name ?
                            <>{GetText("EmailForm_From")} {company.name} {"<" + company.email + ">"}</> :
                            <>{GetText("EmailForm_From")} {user.firstName} {user.lastName} {"<" + user.email + ">"}</>}
                    </Form.Item>

                    <div style={{
                        backgroundColor: '#fafafa',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8',
                        marginBottom: '30px'
                    }}>
                        <h3>{!hasNoMembersOrEmails() ? <CheckCircleFilled style={{ color: "green" }} /> : <CheckCircleOutlined style={{ color: "grey" }} />} {GetText("EmailForm_Step1")}</h3>
                        <Divider style={{ marginTop: "5px" }} />
                        <span style={{ color: "red" }}>*</span> {GetText("EmailForm_ChooseReceiver")}
                        {props.type === EmailFormType.EmailFormByGroup && props.groups.length > 0 ? <><br />
                            <Select placeholder={GetText("EmailForm_ChooseCategories")} onChange={handleGroupChange} style={{ width: '100%', maxWidth: '400px', marginBottom: "10px" }} mode="multiple" options={groupOptions()}></Select>
                            {props.membershipProducts?.length > 0 ? <div><Select
                                placeholder={GetText("EmailForm_ChooseMembershipProducts")}
                                mode="multiple"
                                allowClear
                                style={{ width: '100%', maxWidth: '400px', marginBottom: "10px" }}
                                onChange={(value: number[]) => setSelectedMembershipProductIds(value)}
                                options={props.membershipProducts.map((p: any) => ({ label: p.name, value: p.id }))}
                            /></div> : ""}
                            {props.user.paymentConfiguration == PaymentConfiguration.YearlyPayments ? <div><BookySelectPaymentStatus showLastYearOptions={true} style={{ width: '100%', maxWidth: '400px' }} onSelectPaymentChanged={onSelectPaymentChanged} /></div> : ""}
                            {familyMembersEnabled ? <div style={{ marginBottom: "10px" }}>
                                <span style={{ fontSize: 12, color: '#8c8c8c' }}>{GetText("Communication_RecipientTypeLabel")}</span>
                                <Select
                                    value={recipientType}
                                    style={{ width: '100%', maxWidth: '400px', display: 'block' }}
                                    onChange={(value: RecipientTypeFilter) => setRecipientType(value)}
                                    options={[
                                        { label: GetText("Communication_RecipientAll"), value: "all" },
                                        { label: GetText("Communication_RecipientPrimariesOnly"), value: "primaries" },
                                        { label: GetText("Communication_RecipientFamilyOnly"), value: "family" },
                                    ]}
                                />
                            </div> : ""}
                            <br /><BookyCheckbox noMarginBottom alignLabelRight label={GetText("EmailForm_OverrideEmailSetting")} name="emailNotificationPreference"></BookyCheckbox>
                            {(user?.subscriptionLevel === SubscriptionLevel.Premium || user?.subscriptionLevel === SubscriptionLevel.Enterprise) && (
                                <AdvancedMemberFilter
                                    memberFieldSettings={memberFieldSettings}
                                    onFilterChange={handleAdvancedFilterChange}
                                    hideApplyButton={true}
                                />
                            )}
                        </> : ""}

                        {props.type === EmailFormType.EmailFormByMember ? <MemberAutocomplete showList={false} AutoCompleteType={AutoCompleteType.Email} onAutoCompleteSelect={onAutoCompleteSelect} listHeader={GetText("ReminderForm_ReminderListHeader")} members={props.memberResult.members} /> : ""}
                        {props.type === EmailFormType.EmailFormByGroup ? <><br /><BookyButton style={{ marginTop: "10px" }} text={GetText("EmailForm_CreateSendList")} onClick={() => onFilterMembers(currentFilterRequest)}></BookyButton></> : ""}
                        {props.type === EmailFormType.EmailFormByExternal ? <><br /> <ContactAutoComplete AutoCompleteType={AutoCompleteType.Email} descriptionText={GetText("AutoComplete_HelpTextAddEmail")} showList listHeader={GetText("EmailForm_ExternalMailHeader")} emails={externalEmails} onEmailsUpdated={onEmailsUpdated} /></> : ""}

                        {selectedMembers.length === 0 ? "" : <MemberTable user={user} memberFieldSettings={memberFieldSettings} isMail={true} members={selectedMembers} style={{ width: '100%', marginTop: "10px" }} removeMember={removeMember} showPaymentLinkColumnLastYear={getRequiredPaymentLinkTypes(html).lastYear} showPaymentLinkColumnNextYear={getRequiredPaymentLinkTypes(html).nextYear} showPaymentLinkColumnCurrentYear={getRequiredPaymentLinkTypes(html).currentYear} />}
                        {selectedMembers.length > 0 ? <><br /><i>{selectedMembers.length} {GetText("EmailForm_ChosenReceivers")}</i></> : ""}

                        {getMembersWithoutPaymentLink().length > 0 && (
                            <Alert
                                style={{ marginTop: 12 }}
                                type="warning"
                                showIcon
                                icon={<ExclamationCircleFilled style={{ color: '#faad14' }} />}
                                message={GetText("EmailForm_PaymentLinkWarning").replace("{0}", getMembersWithoutPaymentLink().length.toString())}
                                description={
                                    <div style={{ marginTop: 4 }}>
                                        {getMembersWithoutPaymentLink().slice(0, 10).map((m, idx) => (
                                            <div key={m.memberId ?? idx} style={{ padding: '2px 0' }}>
                                                {m.firstName} {m.lastName} {m.email ? `(${m.email})` : ''} {m.uniqueId ? `- ${m.uniqueId}` : ''}
                                            </div>
                                        ))}
                                        {getMembersWithoutPaymentLink().length > 10 && (
                                            <div style={{ padding: '4px 0', fontStyle: 'italic', color: '#888' }}>
                                                {GetText("EmailForm_PaymentLinkWarningMore").replace("{0}", (getMembersWithoutPaymentLink().length - 10).toString())}
                                            </div>
                                        )}
                                        <div style={{ padding: '4px 0', fontStyle: 'italic', color: '#666', marginTop: 4 }}>
                                            {GetText("EmailForm_PaymentLinkSeeTable")}
                                        </div>
                                    </div>
                                }
                            />
                        )}
                    </div>

                    <div style={{
                        backgroundColor: '#fafafa',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8'
                    }}>
                        <h3 style={{ marginTop: "0px" }}>{emailIsvalid ? <CheckCircleFilled style={{ color: "green" }} /> : <CheckCircleOutlined style={{ color: "grey" }} />}  {GetText("EmailForm_Step2")}</h3>
                        <Divider style={{ margin: "5px" }} />
                        <BookySelectDropDown allowClear={true} placeholder={GetText("EmailForm_LoadFromEmailTemplate")} handleChange={handleEmailTemplateChange} style={{ width: '100%', maxWidth: '400px', marginTop: "20px" }} options={emailTemplatesOptions()}></BookySelectDropDown>

                        <BookyCheckbox alignLabelRight label={designInfoLabel} name="enableEmailDesign" onChange={onEmailDesignToggle}></BookyCheckbox>

                        {hasEmailDesign ? <>
                            <Form.Item
                                label={GetText("CreateTemplate_DesignTemplate")}
                                name="emailDesignTemplate"
                                rules={[{ required: true, message: GetText("CreateTemplate_RequiredText") }]}
                            >
                                <EmailDesignTemplateCardSelect onTemplateSelected={onEmailDesignTemplateChange} />
                            </Form.Item>

                            {selectedEmailDesignTemplate === EmailTemplateType.Classic ? <>
                                <Form.Item
                                    name="imageUrl"
                                    hidden
                                    dependencies={["enableEmailDesign", "emailDesignTemplate"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue("enableEmailDesign") && getFieldValue("emailDesignTemplate") === EmailTemplateType.Classic && !value) {
                                                    return Promise.reject(new Error(GetText("CreateTemplate_LogoRequired")));
                                                }

                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_Logo")}>
                                    <Button onClick={openImagePicker}>{GetText("CreateTemplate_SelectLogo")}</Button>
                                </Form.Item>

                                {selectedLogo ? <Image width={220} height={140} preview={true} src={selectedLogo}></Image> : ""}
                            </> : ""}

                            {selectedEmailDesignTemplate === EmailTemplateType.Header ? <>
                                <BookyInput name="header" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Header")} style={{ width: '100%', maxWidth: '400px' }} />

                                <Form.Item label={GetText("CreateTemplate_AccentColor")}>
                                    <ColorPicker color={accentColor} onChangeComplete={onAccentColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="accentColor">
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_TextColor")}>
                                    <ColorPicker color={textColor} onChangeComplete={onTextColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="textColor">
                                    <Input />
                                </Form.Item>
                            </> : ""}
                        </> : ""}

                        <BookyInput onChange={onSubjectchange} label={GetText("EmailForm_Subject")} name="subject" style={{ width: '100%', maxWidth: '400px', marginTop: "10px" }} requiredMessage={GetText("EmailForm_SubjectRequired")} ></BookyInput>

                        {/* Workaround to get editor to refresh value */}
                        {initialHtml ? <OneFrontHtmlEditor key={editorKey} hideVariablesMenu={props.type === EmailFormType.EmailFormByExternal} enablePaymentLink={props.enablePaymentLink} initialHtml={initialHtml} onHtmlChange={onHtmlChange} label={GetText("EmailForm_Message")} name="messageFromTemplate" requiredMessage={GetText("EmailForm_MessageRequired")} /> :
                            <OneFrontHtmlEditor key={editorKey} hideVariablesMenu={props.type === EmailFormType.EmailFormByExternal} enablePaymentLink={props.enablePaymentLink} initialHtml={initialHtml} onHtmlChange={onHtmlChange} label={GetText("EmailForm_Message")} name="message" requiredMessage={GetText("EmailForm_MessageRequired")} />}

                        <Form.Item
                            name="upload"
                            label={GetText("EmailForm_AttachFile")}
                        >
                            <FileSelect key={fileKey} onFileSelectChange={onFileSelectChange} />
                        </Form.Item>

                        <Form.Item>
                            <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                                <Button onClick={onPreviewEmail} loading={previewLoading}>
                                    {GetText("EmailForm_Preview")}
                                </Button>
                                <Button onClick={onOpenTestSendModal} loading={testSendLoading}>
                                    {GetText("EmailForm_TestSend")}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {GetText("EmailForm_SendEmailToGroupButtonText")}
                                </Button>
                            </Space>
                        </Form.Item>
                    </div>

                </Form> : <Alert
                    message={GetText("EmailForm_AlertSuspendedMessage")}
                    description={GetText("EmailForm_AlertSuspendedDescription")}
                    type="warning"
                />}
            </> : <span><Tag color="#1890ff">Premium</Tag><a href={"/" + getCurrentLanguage() + "/Pricing"}>{GetText("Common_UpgradeToPremium")}</a>{GetText("EmailForm_PremiumSendingMessageToGroups")}</span>
        }

        <Modal
            title={GetText("MyEventDetails_SelectImage")}
            width={850}
            open={isImageModalOpen}
            onOk={() => { setIsImageModalOpen(false) }}
            onCancel={() => { setIsImageModalOpen(false) }}
        >
            <ImageSelect images={images} isPublicImage={true} loadImages={loadImages} onImageSelect={onImageSelect} selectedImage={selectedLogo} />
        </Modal>

        <Modal
            title={GetText("EmailForm_Preview")}
            width={900}
            open={isPreviewModalOpen}
            footer={null}
            onCancel={() => { setIsPreviewModalOpen(false) }}
        >
            {previewLoading || !previewHtml ?
                <div
                    style={{
                        width: "100%",
                        height: "620px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #f0f0f0",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff"
                    }}
                >
                    {GetText("EmailForm_PreviewLoading")}
                </div>
                :
                <iframe
                    key={previewRenderKey}
                    title={GetText("EmailForm_Preview")}
                    srcDoc={previewHtml}
                    sandbox=""
                    style={{
                        width: "100%",
                        height: "620px",
                        border: "1px solid #f0f0f0",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff"
                    }}
                />}
        </Modal>

        <Modal
            title={GetText("CreateTemplate_AddDesignInfoTitle")}
            open={isDesignInfoModalOpen}
            onCancel={() => { setIsDesignInfoModalOpen(false) }}
            footer={[
                <Button key="close" onClick={() => { setIsDesignInfoModalOpen(false) }}>
                    {GetText("CreateTemplate_CancelText")}
                </Button>
            ]}
        >
            <p>{GetText("CreateTemplate_AddDesignInfoText1")}</p>
            <p>{GetText("CreateTemplate_AddDesignInfoText2")}</p>
            <p>{GetText("CreateTemplate_AddDesignInfoText3")}</p>
            <p>{GetText("CreateTemplate_AddDesignInfoText4")}</p>
        </Modal>

        <Modal
            title={GetText("EmailForm_TestSendTitle")}
            open={isTestSendModalOpen}
            onOk={onTestSendConfirm}
            confirmLoading={testSendLoading}
            onCancel={onTestSendCancel}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {props.type !== EmailFormType.EmailFormByExternal && (
                    <div>
                        <p><strong>{GetText("EmailForm_SelectMemberForTestSend")}</strong></p>
                        {!testSendMember ? (
                            <MemberAutocomplete
                                showList={false}
                                AutoCompleteType={AutoCompleteType.Email}
                                onAutoCompleteSelect={onTestSendMemberSelect}
                                listHeader={GetText("EmailForm_SelectMemberForTestSend")}
                                members={testSendMembers}
                            />
                        ) : (
                            <div style={{ padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{testSendMember.firstName} {testSendMember.lastName} {testSendMember.email ? `(${testSendMember.email})` : ''}</span>
                                <Button size="small" type="link" onClick={() => setTestSendMember(undefined)}>
                                    {GetText("EmailForm_ChangeSelection")}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {props.type === EmailFormType.EmailFormByExternal && (
                    <div>
                        <p><strong>{GetText("EmailForm_OrEnterExternalEmail")}</strong></p>
                        <Input
                            type="email"
                            placeholder="example@email.com"
                            value={testSendExternalEmail}
                            onChange={(e) => {
                                setTestSendExternalEmail(e.target.value);
                                setTestSendMember(undefined);
                            }}
                        />
                    </div>
                )}
            </Space>
        </Modal>

    </>
}